import type { RefreshResponse } from "@shared/types";
import axios, { type AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from "axios";
import { useAuthStore } from "@/modules/auth/store/auth.store";
import { authEndpoints } from "./endpoints";

export const apiClient = axios.create({
	headers: {
		"Content-Type": "application/json",
	},
	timeout: 30000,
});

// ─── Request Interceptor ────────────────────────────────────────────
apiClient.interceptors.request.use(
	(config: InternalAxiosRequestConfig) => {
		const accessToken = useAuthStore.getState().accessToken;
		if (accessToken) {
			config.headers.Authorization = `Bearer ${accessToken}`;
		}
		return config;
	},
	(error) => Promise.reject(error)
);

// ─── Response Interceptor ───────────────────────────────────────────
// 401 bo'lganda token refresh + concurrent request queue

type FailedRequest = {
	resolve: (token: string) => void;
	reject: (error: unknown) => void;
};

let isRefreshing = false;
let failedQueue: FailedRequest[] = [];

/**
 * Navbatdagi barcha so'rovlarni qayta ishlash
 * Agar token yangilangan bo'lsa — har biriga yangi token bilan resolve
 * Agar xato bo'lsa — har birini reject
 */
function processQueue(error: unknown, token: string | null = null): void {
	for (const request of failedQueue) {
		if (error) {
			request.reject(error);
		} else if (token) {
			request.resolve(token);
		}
	}
	failedQueue = [];
}

apiClient.interceptors.response.use(
	(response: AxiosResponse) => response,
	async (error: AxiosError) => {
		const originalRequest = error.config as InternalAxiosRequestConfig & {
			_retry?: boolean;
		};

		// Faqat 401 va birinchi urinish bo'lgandagina refresh qilamiz
		if (error.response?.status !== 401 || originalRequest._retry) {
			return Promise.reject(error);
		}

		// Login/logout — 401 bu "noto'g'ri parol" yoki "invalid", refresh kerak emas
		if (
			originalRequest.url?.includes("/auth/login") ||
			originalRequest.url?.includes("/auth/logout")
		) {
			return Promise.reject(error);
		}

		// Refresh endpoint'ning o'zi 401 bersa — loop'dan saqlanish
		if (originalRequest.url?.includes("/auth/refresh")) {
			useAuthStore.getState().logout();
			window.location.href = "/login";
			return Promise.reject(error);
		}

		originalRequest._retry = true;

		// Agar allaqachon refresh jarayonida bo'lsak — navbatga qo'shamiz
		if (isRefreshing) {
			return new Promise<string>((resolve, reject) => {
				failedQueue.push({ resolve, reject });
			}).then((newToken) => {
				originalRequest.headers.Authorization = `Bearer ${newToken}`;
				return apiClient(originalRequest);
			});
		}

		isRefreshing = true;

		const refreshToken = useAuthStore.getState().refreshToken;

		if (!refreshToken) {
			isRefreshing = false;
			useAuthStore.getState().logout();
			window.location.href = "/login";
			return Promise.reject(error);
		}

		try {
			// Refresh uchun alohida axios instance ishlatamiz (interceptor loop'dan himoya)
			const response = await axios.post<RefreshResponse>(authEndpoints.refresh, { refreshToken });

			const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data.data;

			// Store'ni yangilash (faqat tokenlarni, user.organizations ni yo'qotmaslik uchun)
			useAuthStore.getState().setTokens(newAccessToken, newRefreshToken);

			// Navbatdagi barcha so'rovlarga yangi token berish
			processQueue(null, newAccessToken);

			// Original so'rovni yangi token bilan qaytadan yuborish
			originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
			return apiClient(originalRequest);
		} catch (refreshError) {
			// Refresh muvaffaqiyatsiz — barcha navbatni reject qilish va logout
			processQueue(refreshError, null);
			useAuthStore.getState().logout();
			window.location.href = "/login";
			return Promise.reject(refreshError);
		} finally {
			isRefreshing = false;
		}
	}
);

export default apiClient;
